import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';

import { apiFetch } from '../lib/api';

type ServerKey = string;

type SideStats = {
  wins: { red: number; blue: number };
  metrics: {
    attendance: { red: number; blue: number };
    frags: { red: number; blue: number };
    tk: { red: number; blue: number };
    fragsPerAttendance: { red: number; blue: number };
    techEfficiency: { red: number; blue: number };
    infantryEfficiency: { red: number; blue: number };
  };
};

type SideTableRow = {
  squad: string;
  kills: number;
  t_kills: number;
  attendance: number;
  avg_att: number;
  slots: number;
  ks_kpd: number;
  efficiency_inf: number;
  efficiency_w: number;
};

type SideStatsResponse = SideStats & {
  server: string;
  recent_replays: Array<{
    squad: string;
    winner: string;
    side: 'red' | 'blue';
    result_type: string;
    rep_link: string;
    replay_name: string;
    replay_date: string;
    rank: number;
  }>;
  tables: {
    red: SideTableRow[];
    blue: SideTableRow[];
  };
};

const metricLabels: { key: keyof SideStats['metrics']; label: string }[] = [
  { key: 'attendance', label: 'Посещаемость' },
  { key: 'frags', label: 'Фраги' },
  { key: 'tk', label: 'ТК' },
  { key: 'fragsPerAttendance', label: 'Фр./Пос.' },
  { key: 'techEfficiency', label: 'КПД Техника' },
  { key: 'infantryEfficiency', label: 'КПД Пехота' },
];

function pickRandom<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

export function SideStatsPage({
  server,
  onSelectServer,
  theme,
}: {
  server: ServerKey | null;
  onSelectServer: (server: ServerKey) => void;
  theme: 'light' | 'dark';
}): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const [activeSide, setActiveSide] = useState<'red' | 'blue'>('blue');
  const [tableSortKey, setTableSortKey] = useState<keyof SideTableRow>('efficiency_w');
  const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('desc');

  const { data: serverResponse, isLoading: serversLoading } = useQuery({
    queryKey: ['side-servers'],
    queryFn: () => apiFetch<{ servers: string[] }>('/servers/'),
  });
  const { data: sideStats, isLoading: statsLoading } = useQuery({
    queryKey: ['side-stats', server],
    queryFn: () => apiFetch<SideStatsResponse>(`/side-stats/${encodeURIComponent(server ?? '')}/`),
    enabled: Boolean(server),
  });

  const servers = useMemo(() => {
    const list = serverResponse?.servers ?? [];
    return list.map((srv) => ({
      key: srv.toLowerCase(),
      label: srv.toUpperCase(),
    }));
  }, [serverResponse?.servers]);

  const selectedStats = server ? sideStats ?? null : null;
  const activeServerLabel =
    servers.find((srv) => srv.key === server)?.label ?? (server ? server.toUpperCase() : null);

  const recentReplays = sideStats?.recent_replays ?? [];
  const activeTableRows = selectedStats?.tables?.[activeSide] ?? [];

  const sideTableColumns = useMemo<
    Array<{ key: keyof SideTableRow; label: string; align: 'text-left' | 'text-center' }>
  >(
    () => [
      { key: 'squad', label: 'Отряд', align: 'text-left' },
      { key: 'kills', label: 'Фраги', align: 'text-center' },
      { key: 't_kills', label: 'ТК', align: 'text-center' },
      { key: 'attendance', label: 'Посещаемость', align: 'text-center' },
      { key: 'avg_att', label: 'Ср. пос.', align: 'text-center' },
      { key: 'slots', label: 'Слоты', align: 'text-center' },
      { key: 'ks_kpd', label: 'КС КПД', align: 'text-center' },
      { key: 'efficiency_inf', label: 'КПД Пех.', align: 'text-center' },
      { key: 'efficiency_w', label: 'КПД Тех.', align: 'text-center' },
    ],
    []
  );

  const bannerImages = useMemo(() => {
      return {
        red: pickRandom(['/img/500_red_1.jpg', '/img/500_red_2.jpg']),
        blue: pickRandom(['/img/500_blue_1.jpg', '/img/500_blue_2.jpg']),
      };
    }, [server]);

  const formatValue = (value: number) => (Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1));
  const formatTableValue = (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(2));
  const renderTableSortIndicator = (key: keyof SideTableRow) => {
    if (tableSortKey !== key) return '↕';
    return tableSortDirection === 'asc' ? '↑' : '↓';
  };

  useEffect(() => {
    if (!chartRef.current || !selectedStats) return;
    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    const styles = getComputedStyle(document.documentElement);
    const toHsl = (token: string, fallback?: string) => {
      const value = styles.getPropertyValue(token);
      return value ? `hsl(${value.trim()})` : fallback ?? '#9ca3af';
    };
    const axisColor = toHsl('--border');
    const foreground = toHsl('--foreground');
    const red = theme === 'dark' ? '#eb5757' : '#c53030';
    const blue = theme === 'dark' ? '#2f80ed' : '#1d4ed8';
    const neutral = toHsl('--muted', '#9ca3af');

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const diffData = metricLabels.map((metric) => {
      const value = selectedStats.metrics[metric.key];
      const redValue = value.red;
      const blueValue = value.blue;
      const diff = blueValue - redValue;
      const absDiff = Math.abs(diff);

      if (diff > 0) {
        return {
          name: metric.label,
          value: diff,
          side: 'blue' as const,
          displayValue: formatValue(absDiff),
          redValue,
          blueValue,
          itemStyle: { color: blue },
          label: { color: foreground },
        };
      }

      if (diff < 0) {
        return {
          name: metric.label,
          value: diff,
          side: 'red' as const,
          displayValue: formatValue(absDiff),
          redValue,
          blueValue,
          itemStyle: { color: red },
          label: { color: foreground },
        };
      }

      return {
        name: metric.label,
        value: 0,
        side: 'even' as const,
        displayValue: formatValue(redValue),
        redValue,
        blueValue,
        itemStyle: { color: neutral },
        label: { color: foreground },
      };
    });

    const maxAbsValue = Math.max(...diffData.map((item) => Math.abs(item.value)), 1);
    const axisLimit = maxAbsValue * 1.2;

    const option: echarts.EChartsOption = {
      tooltip: {
        show: true,
        trigger: 'item',
        backgroundColor: toHsl('--card', 'rgba(15, 23, 42, 0.9)'),
        borderColor: axisColor,
        textStyle: { color: foreground },
      },
      grid: { left: 160, right: 160, top: 24, bottom: 24, containLabel: true },
      xAxis: {
        type: 'value',
        min: -axisLimit,
        max: axisLimit,
        splitLine: { lineStyle: { color: axisColor, type: 'dashed' } },
        axisLabel: { show: false },
        axisLine: { lineStyle: { color: axisColor } },
      },
      yAxis: {
        type: 'category',
        data: metricLabels.map((m) => m.label),
        axisLabel: {
          color: foreground,
          fontWeight: 700,
          fontSize: 13,
          width: 140,
          overflow: 'break',
          lineHeight: 18,
        },
        axisLine: { lineStyle: { color: axisColor } },
      },
      series: [
        {
          type: 'bar',
          data: diffData,
          barWidth: 20,
          barGap: '40%',
          label: { show: false },
          itemStyle: { borderRadius: 6 },
          tooltip: {
            trigger: 'item',
            formatter: ({ data }) => {
              const item = data as {
                name: string;
                side: 'red' | 'blue' | 'even';
                redValue: number;
                blueValue: number;
                displayValue: string;
                value: number;
              };
              const winner =
                item.side === 'even'
                  ? 'Равные показатели'
                  : item.side === 'red'
                    ? 'Преимущество Красных'
                    : 'Преимущество Синих';
              const advantage = item.side === 'even' ? item.displayValue : formatValue(Math.abs(item.value));
              return [
                `<strong>${item.name}</strong>`,
                `${winner}: ${advantage}`,
                `Красные: ${formatValue(item.redValue)}`,
                `Синие: ${formatValue(item.blueValue)}`,
              ].join('<br/>');
            },
          },
        },
      ],
      animationDuration: 400,
    };

    chartInstance.current.setOption(option, true);
    chartInstance.current.resize();

    const resize = () => chartInstance.current?.resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [selectedStats, theme]);

  useEffect(() => {
    setActiveSide('blue');
    setTableSortKey('efficiency_w');
    setTableSortDirection('desc');
  }, [server]);

  const sortedTableRows = useMemo(() => {
    const rows = [...activeTableRows];
    rows.sort((a, b) => {
      const aVal = a[tableSortKey];
      const bVal = b[tableSortKey];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        const diff = aVal - bVal;
        return tableSortDirection === 'asc' ? diff : -diff;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr === bStr) return 0;
      const result = aStr > bStr ? 1 : -1;
      return tableSortDirection === 'asc' ? result : -result;
    });
    return rows;
  }, [activeTableRows, tableSortDirection, tableSortKey]);

  const handleTableSort = (key: keyof SideTableRow) => {
    setTableSortKey(key);
    setTableSortDirection((prev) => (key === tableSortKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
  };

  return (
    <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          {selectedStats ? (
            <>
              <p className="text-sm text-muted-foreground">Показатели выбранного сервера</p>
              <p className="text-2xl font-semibold">Статистика сторон {activeServerLabel}</p>
            </>
          ) : statsLoading ? (
            <>
              <p className="text-sm text-muted-foreground">Загрузка данных сервера</p>
              <p className="text-2xl font-semibold">Статистика сторон</p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Выберите сервер</p>
              <p className="text-2xl font-semibold">Статистика сторон</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {servers.map((srv) => (
            <button
              key={srv.key}
              type="button"
              onClick={() => onSelectServer(srv.key)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition ${
                server === srv.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:border-primary'
              }`}
            >
              Сервер {srv.label}
            </button>
          ))}
        </div>
      </div>

      {selectedStats ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {([
              { value: selectedStats.wins.red, img: bannerImages.red },
              { value: selectedStats.wins.blue, img: bannerImages.blue },
            ] as const).map((item, idx) => (
              <div
                key={idx}
                className="relative aspect-[3/2] overflow-hidden rounded-xl border border-border bg-card shadow"
                style={{ backgroundImage: `url(${item.img})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                <div className="absolute inset-0 bg-background/70" />
                <div className="relative flex h-full items-center justify-center p-4 sm:p-6">
                  <p className="text-6xl font-black text-foreground drop-shadow-lg sm:text-7xl">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {recentReplays.length > 0 ? (
              recentReplays.map((replay) => {
              const themeClasses =
                replay.side === 'red'
                  ? 'border-red-500/40 bg-red-500/10'
                  : replay.side === 'blue'
                    ? 'border-blue-500/40 bg-blue-500/10'
                    : 'border-border bg-muted/30';
              const accentText = replay.side === 'red' ? 'text-red-600 dark:text-red-200' : replay.side === 'blue' ? 'text-blue-600 dark:text-blue-200' : 'text-foreground';

              return (
                <div key={`${replay.replay_date}-${replay.replay_name}`} className={`rounded-lg border p-4 shadow-sm ${themeClasses}`}>
                  <a
                    href={replay.rep_link}
                    target="_blank"
                    rel="noreferrer"
                    className="line-clamp-2 text-sm font-semibold text-foreground underline-offset-4 hover:underline"
                  >
                    {replay.replay_name}
                  </a>
                  <p className="mt-1 text-xs text-muted-foreground">{replay.replay_date}</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <p className="text-muted-foreground">Игрок-победитель</p>
                    <p className={`font-semibold ${accentText}`}>{replay.winner}</p>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <p className="text-muted-foreground">Тип победы</p>
                    <p className={`font-semibold ${accentText}`}>{replay.result_type}</p>
                  </div>
                </div>
              );
              })
            ) : (
              <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                Пока нет данных о последних победах.
              </div>
            )}
          </div>

          <div className="card space-y-3 p-4">
            <div>
              <p className="text-sm text-muted-foreground">Разница по ключевым показателям</p>
              <p className="text-2xl font-semibold">Сравнение сторон</p>
            </div>
            <div ref={chartRef} className="h-[360px] w-full" />
          </div>

          <div className="card space-y-4 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Показатели отрядов по сторонам</p>
                <p className="text-2xl font-semibold">Составы сторон</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'blue', label: 'Синие' },
                  { key: 'red', label: 'Красные' },
                ] as const).map((tab) => {
                  const isActive = activeSide === tab.key;
                  const themeClasses =
                    tab.key === 'blue'
                      ? isActive
                        ? 'border-blue-500/60 bg-blue-500/10 text-blue-600 dark:text-blue-200'
                        : 'border-border text-muted-foreground hover:border-blue-500/60 hover:text-blue-600'
                      : isActive
                        ? 'border-red-500/60 bg-red-500/10 text-red-600 dark:text-red-200'
                        : 'border-border text-muted-foreground hover:border-red-500/60 hover:text-red-600';

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveSide(tab.key)}
                      className={`rounded-md border px-4 py-2 text-sm font-semibold transition ${themeClasses}`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {sortedTableRows.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {sideTableColumns.map((col) => (
                        <th key={col.key} className={`px-3 py-3 ${col.align} font-semibold`}>
                          <button
                            type="button"
                            onClick={() => handleTableSort(col.key)}
                            className={`inline-flex items-center gap-2 ${col.align === 'text-left' ? '' : 'mx-auto'}`}
                          >
                            <span>{col.label}</span>
                            <span className="text-muted-foreground">{renderTableSortIndicator(col.key)}</span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTableRows.map((row) => (
                      <tr key={row.squad} className="border-t border-border">
                        {sideTableColumns.map((col) => {
                          const value = row[col.key as keyof SideTableRow];
                          return (
                            <td key={col.key} className={`px-3 py-3 ${col.align}`}>
                              {col.key === 'squad' ? (
                                <a
                                  href={`/squad-stats/${encodeURIComponent(String(value))}`}
                                  className="font-semibold text-foreground hover:underline"
                                >
                                  {value}
                                </a>
                              ) : typeof value === 'number' ? (
                                formatTableValue(value)
                              ) : (
                                value
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                Нет данных для выбранной стороны.
              </div>
            )}
          </div>
        </div>
      ) : servers.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {servers.map((srv) => (
            <button
              key={srv.key}
              type="button"
              onClick={() => onSelectServer(srv.key)}
              className="card flex h-full flex-col items-start gap-2 p-4 text-left transition hover:-translate-y-1 hover:ring-2 hover:ring-accent/50"
            >
              <p className="text-sm text-muted-foreground">Открыть статистику сервера</p>
              <p className="text-2xl font-semibold">Сервер {srv.label}</p>
              <p className="text-sm text-muted-foreground">Красные и Синие — победы и ключевые показатели</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          {serversLoading ? 'Загрузка списка серверов…' : 'Нет доступных серверов.'}
        </div>
      )}
    </main>
  );
}
